import * as echarts from "echarts";
import type { ECharts, EChartsOption } from "echarts";
import type { PriceSalesDistributionVo, SalesRegionVo, TrendDataItem, VisitorRegionVo } from "@zhihaoscm/entity";
import { formatCurrency } from "@zhihaoscm/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatNumber, toThousand } from "@/utils/number";

const priceColors = ["#1777FF", "#FFAE4C", "#FFE24C", "#6FD195"];

interface AxisTooltipParam {
    axisValue?: string | number;
    name?: string;
    seriesName?: string;
    value?: unknown;
}

/** 将 ECharts 的单项/多项参数统一成旧 Vue formatter 使用的数组形状。 */
function normalizeTooltipParams(params: unknown): AxisTooltipParam[] {
    return (Array.isArray(params) ? params : [params]) as AxisTooltipParam[];
}

/** 保留旧 Vue 对金额、吨数的两位小数和千分位规则。 */
function formatChartCount(value: unknown): string | number {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return "--";
    return numberValue === 0 ? 0 : formatNumber(numberValue, 2);
}

/** 生成详情页图表统一的 tooltip 行，单位作为独立节点避免被 ECharts 丢弃。 */
function renderTooltipItem(
    seriesName: string,
    value: string | number,
    unit: string,
    color: string,
    icon: "bar" | "line" | "none" = "bar"
) {
    const iconMarkup =
        icon === "none"
            ? `<span class="tooltip-icon tooltip-icon-empty"></span>`
            : icon === "line"
              ? `<span class="tooltip-icon tooltip-icon-line" style="color:${color}"></span>`
              : `<span class="tooltip-icon" style="background-color:${color}"></span>`;
    return `<div class="tooltip-item">${iconMarkup}<span class="tooltip-item-label">${seriesName}</span><span class="tooltip-item-value"><span class="value">${value}</span><span class="unit">${unit}</span></span></div>`;
}

interface SalesRegionTooltipConfig {
    unit: string;
    color: string;
    formatValue: (value: unknown) => string | number;
}

const salesRegionTooltipMap: Record<string, SalesRegionTooltipConfig> = {
    总销售量: { unit: "吨", color: "#3370FF", formatValue: formatChartCount },
    生成订单: { unit: "个", color: "#7FD1EF", formatValue: value => toThousand(Number(value)) }
};

/** 复用旧 Vue 销售地域图的系列、颜色与坐标轴约定。 */
export function buildConsignmentSalesRegionOption(data: SalesRegionVo[] = []): EChartsOption {
    return {
        grid: { left: 20, right: 20, bottom: "20%", top: 16, containLabel: true },
        tooltip: {
            trigger: "axis",
            confine: true,
            className: "consignment-chart-tooltip",
            formatter: params => {
                const normalizedParams = normalizeTooltipParams(params);
                let html = `<div class="tooltip-title">${normalizedParams[0]?.axisValue ?? ""}</div>`;
                normalizedParams.forEach(param => {
                    const config = salesRegionTooltipMap[param.seriesName || ""];
                    if (!config) return;
                    html += renderTooltipItem(
                        param.seriesName || "",
                        config.formatValue(param.value),
                        config.unit,
                        config.color
                    );
                });
                return html;
            }
        },
        legend: {
            show: true,
            top: "bottom",
            bottom: "auto",
            padding: [0, 0, 15, 0],
            icon: "rect",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: { fontSize: 14, lineHeight: 14, padding: [0, 0, -2, 0] }
        },
        xAxis: {
            type: "category",
            data: data.map(item => item.areaName),
            axisLabel: { interval: 0, color: "#000000b3" },
            axisTick: { show: false },
            axisLine: { lineStyle: { color: "#F0F0F0" } }
        },
        yAxis: {
            type: "value",
            splitNumber: 5,
            axisLabel: { color: "#000000b3" },
            axisLine: { show: true, lineStyle: { color: "#F0F0F0" } },
            splitLine: { show: true, lineStyle: { type: "dashed", color: "#D9D9D9" } }
        },
        series: [
            {
                name: "总销售量",
                type: "bar",
                data: data.map(item => item.totalSalesVolume),
                barWidth: 16,
                itemStyle: { color: "#3370FF", borderRadius: 2 }
            },
            {
                name: "生成订单",
                type: "bar",
                data: data.map(item => item.orderCount),
                barWidth: 16,
                itemStyle: { color: "#7FD1EF", borderRadius: 2 }
            }
        ]
    };
}

/** 复用旧 Vue 访客地域图的单柱样式和固定柱宽。 */
export function buildConsignmentVisitorRegionOption(data: VisitorRegionVo[] = []): EChartsOption {
    return {
        tooltip: {
            trigger: "axis",
            confine: true,
            className: "consignment-chart-tooltip",
            formatter: params => {
                const normalizedParams = normalizeTooltipParams(params);
                const areaName = normalizedParams[0]?.name ?? normalizedParams[0]?.axisValue ?? "";
                const current = data.find(item => item.areaName === areaName);
                const visitorCount = current?.visitorCount ?? normalizedParams[0]?.value;
                const rawProportion = Number(current?.proportion ?? 0);
                const proportion = Number.isFinite(rawProportion)
                    ? `${Math.floor(rawProportion <= 1 ? rawProportion * 100 : rawProportion)}%`
                    : "0%";
                return `<div class="tooltip-box"><div class="tooltip-title">${areaName}</div>${renderTooltipItem(
                    "访客人数",
                    formatCurrency(Number(visitorCount)),
                    "人",
                    "#3370FF"
                )}${renderTooltipItem("占比", proportion.replace("%", ""), "%", "#3370FF", "none")}</div>`;
            }
        },
        grid: { left: 0, right: 0, bottom: 30, top: 30, containLabel: true },
        xAxis: {
            type: "category",
            data: data.map(item => item.areaName),
            axisLabel: { interval: 0, color: "#000000b3" },
            axisLine: { lineStyle: { color: "#F0F0F0" } },
            axisTick: { show: false }
        },
        yAxis: {
            type: "value",
            splitNumber: 5,
            splitLine: { lineStyle: { type: "dashed" } },
            axisLine: { show: true, lineStyle: { color: "#F0F0F0" } },
            axisLabel: { color: "#000000b3" }
        },
        series: [
            {
                name: "访客人数",
                type: "bar",
                data: data.map(item => item.visitorCount),
                barWidth: 16,
                itemStyle: { color: "#3370FF", borderRadius: 2 }
            }
        ]
    };
}

/** 按旧 Vue 的多指标组合图映射商品趋势数据，数值仍采用原接口字段。 */
export function buildConsignmentTrendOption(data: TrendDataItem[] = []): EChartsOption {
    const barSeries = [
        { name: "总销售额", key: "totalSalesAmount", color: "#1777FF", unit: "元", formatValue: formatChartCount },
        { name: "退款金额", key: "refundAmount", color: "#EB6C69", unit: "元", formatValue: formatChartCount },
        { name: "结算金额", key: "settlementAmount", color: "#7FD1EF", unit: "元", formatValue: formatChartCount },
        { name: "总销量", key: "totalSalesTon", color: "#F2A750", unit: "吨", formatValue: formatChartCount },
        { name: "结算吨数", key: "settlementTon", color: "#86D59A", unit: "吨", formatValue: formatChartCount },
        {
            name: "订单数",
            key: "orderCount",
            color: "#FFA6E4",
            unit: "笔",
            formatValue: (value: unknown) => toThousand(Number(value))
        }
    ] as const;
    const lineSeries = [
        {
            name: "销售单价",
            key: "salesPrice",
            color: "#A982EC",
            unit: "元/吨",
            axis: 1,
            formatValue: formatChartCount
        },
        {
            name: "访问人数",
            key: "visitUserCount",
            color: "#7FD1EF",
            unit: "人",
            axis: 2,
            formatValue: (value: unknown) => toThousand(Number(value))
        },
        {
            name: "访问次数",
            key: "visitCount",
            color: "#A6B2D0",
            unit: "次",
            axis: 3,
            formatValue: (value: unknown) => toThousand(Number(value))
        },
        {
            name: "转化率",
            key: "conversionRate",
            color: "#3EBA00",
            unit: "%",
            axis: 4,
            formatValue: (value: unknown) => formatCurrency(Number(value))
        }
    ] as const;

    return {
        grid: { left: 20, right: 20, bottom: "26%", top: 20 },
        legend: {
            show: true,
            bottom: 0,
            itemWidth: 16,
            itemHeight: 16,
            data: [...barSeries, ...lineSeries].map(item => item.name)
        },
        tooltip: {
            trigger: "axis",
            confine: true,
            className: "consignment-chart-tooltip",
            formatter: params => {
                const normalizedParams = normalizeTooltipParams(params);
                let html = `<div class="tooltip-title">${normalizedParams[0]?.axisValue ?? ""}</div>`;
                const configMap = new Map<string, (typeof barSeries)[number] | (typeof lineSeries)[number]>(
                    [...barSeries, ...lineSeries].map(item => [item.name, item])
                );
                normalizedParams.forEach(param => {
                    const config = configMap.get(param.seriesName || "");
                    if (!config) return;
                    html += renderTooltipItem(
                        param.seriesName || "",
                        config.formatValue(param.value),
                        config.unit,
                        config.color,
                        "axis" in config ? "line" : "bar"
                    );
                });
                return html;
            }
        },
        xAxis: {
            type: "category",
            data: data.map(item => item.dateStr),
            axisLabel: { interval: 0, color: "#000000b3", hideOverlap: true },
            axisTick: { show: false },
            axisLine: { lineStyle: { color: "#F0F0F0" } }
        },
        yAxis: [{ show: false }, ...lineSeries.map(item => ({ name: item.name, show: false }))],
        dataZoom: [
            {
                brushSelect: false,
                show: true,
                start: 0,
                end: data.length > 20 ? (20 / data.length) * 100 : 100,
                bottom: "10%",
                borderColor: "#ECF2FF",
                backgroundColor: "#fff",
                selectedDataBackground: { lineStyle: { color: "#739AE0" }, areaStyle: { color: "#E3EFFF" } },
                dataBackground: { lineStyle: { color: "#C0D7FF" }, areaStyle: { color: "#EAF3FF" } },
                handleStyle: { borderColor: "#85B1FF" },
                moveHandleStyle: { color: "#85B1FF" }
            }
        ],
        series: [
            ...barSeries.map(item => ({
                name: item.name,
                type: "bar" as const,
                data: data.map(row => row[item.key]),
                barMaxWidth: 20,
                itemStyle: { color: item.color, borderRadius: 2 }
            })),
            ...lineSeries.map(item => ({
                name: item.name,
                type: "line" as const,
                data: data.map(row =>
                    item.key === "conversionRate" ? Math.floor(row[item.key] * 100) : row[item.key]
                ),
                yAxisIndex: item.axis,
                symbol: "none",
                silent: true,
                itemStyle: { color: item.color },
                lineStyle: { color: item.color },
                areaStyle: { opacity: 0.08 }
            }))
        ]
    };
}

interface PriceDistributionItem {
    name: string;
    value: number;
}

/** 把旧版价格区间数据转成环形图数据，保留 0～1 占比语义。 */
function normalizePriceDistribution(items: PriceSalesDistributionVo["distributionList"] = []): PriceDistributionItem[] {
    return items.map(item => ({ name: item.priceRange?.replace("-", "~") || "--", value: Number(item.salesTon) || 0 }));
}

function useResponsiveChart(option: EChartsOption) {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<ECharts | undefined>(undefined);

    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        // 延迟到容器有实际尺寸再创建实例，避免隐藏页签的零宽图表无法恢复。
        const renderChart = () => {
            if (!element.clientWidth || !element.clientHeight) return;
            if (!chartRef.current) chartRef.current = echarts.init(element);
            chartRef.current.setOption(option, true);
            chartRef.current.resize();
        };
        const resizeObserver = new ResizeObserver(renderChart);
        resizeObserver.observe(element);
        window.addEventListener("resize", renderChart);
        renderChart();

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", renderChart);
            chartRef.current?.dispose();
            chartRef.current = undefined;
        };
    }, [option]);

    return { containerRef, chartRef };
}

/** 可访问的商品趋势 ECharts 容器，初始化和尺寸恢复沿用后台看板约定。 */
export function ConsignmentTrendChart({ data }: { data: TrendDataItem[] }) {
    const option = useMemo(() => buildConsignmentTrendOption(data), [data]);
    const { containerRef } = useResponsiveChart(option);
    return <div ref={containerRef} className="consignment-trend-chart" role="img" aria-label="商品趋势分析图" />;
}

/** 地域销售 ECharts 图，与详情页其它图表共享尺寸和销毁逻辑。 */
export function ConsignmentSalesRegionChart({ data }: { data: SalesRegionVo[] }) {
    const option = useMemo(() => buildConsignmentSalesRegionOption(data), [data]);
    const { containerRef } = useResponsiveChart(option);
    return (
        <div ref={containerRef} className="consignment-sales-region-chart" role="img" aria-label="销售情况地域分布图" />
    );
}

/** 访客地域 ECharts 图，与旧版 270px 图表高度保持一致。 */
export function ConsignmentVisitorRegionChart({ data }: { data: VisitorRegionVo[] }) {
    const option = useMemo(() => buildConsignmentVisitorRegionOption(data), [data]);
    const { containerRef } = useResponsiveChart(option);
    return (
        <div ref={containerRef} className="consignment-visitor-region-chart" role="img" aria-label="访客地域分布图" />
    );
}

/** 旧 Vue 价格区间环图与可点击图例，按销售吨数分布显示。 */
export function ConsignmentPriceDistributionChart({
    data,
    totalSalesTon
}: {
    data: PriceSalesDistributionVo["distributionList"];
    totalSalesTon?: number;
}) {
    const items = useMemo(() => normalizePriceDistribution(data), [data]);
    const [hiddenItems, setHiddenItems] = useState<Set<string>>(() => new Set());
    const option = useMemo<EChartsOption>(
        () => ({
            legend: { show: false },
            tooltip: { trigger: "item", confine: true },
            series: [
                {
                    type: "pie",
                    radius: ["65%", "88%"],
                    center: ["50%", "50%"],
                    avoidLabelOverlap: false,
                    label: { show: false },
                    labelLine: { show: false },
                    data: items.map((item, index) => ({
                        ...item,
                        itemStyle: { color: priceColors[index % priceColors.length] }
                    }))
                }
            ]
        }),
        [items]
    );
    const { containerRef, chartRef } = useResponsiveChart(option);
    const toggleItem = (name: string) => {
        const next = new Set(hiddenItems);
        const isHidden = next.has(name);
        if (isHidden) next.delete(name);
        else next.add(name);
        setHiddenItems(next);
        chartRef.current?.dispatchAction({ type: isHidden ? "legendSelect" : "legendUnSelect", name });
    };

    return (
        <div className="price-distribution-chart-wrap">
            <div className="price-distribution-donut">
                <div ref={containerRef} className="price-distribution-chart" role="img" aria-label="价格销量分布图" />
                <div className="donut-total" aria-hidden="true">
                    <span>总销量</span>
                    <strong>{Number(totalSalesTon || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}</strong>
                    <span>吨</span>
                </div>
            </div>
            <div className="price-distribution-legend">
                {items.map((item, index) => (
                    <button
                        key={`${item.name}-${index}`}
                        type="button"
                        aria-pressed={!hiddenItems.has(item.name)}
                        onClick={() => toggleItem(item.name)}
                    >
                        <span
                            className="legend-dot"
                            style={{
                                backgroundColor: hiddenItems.has(item.name)
                                    ? "#CFD2D7"
                                    : priceColors[index % priceColors.length]
                            }}
                        />
                        <span className="legend-range">{item.name}</span>
                        <span className="legend-value">
                            {item.value.toLocaleString("en-US", { maximumFractionDigits: 2 })} 吨
                        </span>
                        <span className="legend-percent">
                            {totalSalesTon ? `${Math.floor((item.value / totalSalesTon) * 100)}%` : "0%"}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}
